import { useNavigate } from "react-router-dom"
import { useMemo, useRef, useState } from "react"
import { FieldType, FileField, FileType, Form, FormField, ModalId, useUIActions, Validatable } from "../../../../../features/ui"
import { ItemValidation, Operation } from "../../../../operations"
import { DeliveryMethod, DeliveryOrder } from "../../../state/types"
import { DeliveryItemLotData, DeliveryItemRow } from "../../types"
import { useActions } from "./actions"
import { Data2, Labels, useDescribeHooks } from "../../../../../features/data-types"
import { BranchBadge } from "../../../../manager"
import { TableName } from "../../../../../tables"
import { DeliveryOrderItemsTable } from "../../delivery-order/delivery-order-items-table/deliveryOrderItemsTable"
import { fileHelpers } from "../../../../../features/files"
import { DefineLotFormModal } from "../define-lot-form-modal/defineLotFormModal"
import { useDeliveryHooks } from "../../../hooks/deliveryHooks"

type Props = {
    deliveryOrder: DeliveryOrder
    operation: Operation
}

export const DeliveryForm = (props: Props) => {
    const { deliveryOrder, operation } = props
    const isReception = useMemo(() => deliveryOrder.method === DeliveryMethod.RECEPTION, [deliveryOrder.method])

    const { deliveryNoteFileName2 } = fileHelpers
    const { describe } = useDescribeHooks()
    const { validateCompleteDeliveryItems } = useDeliveryHooks()
    const navigate = useNavigate()
    const { createDelivery } = useActions()
    const { openModal } = useUIActions()

    const defaultItemRows = useMemo(() => deliveryOrder.items
        .filter(item => item.deliveryStats.pending > 0)
        .map(item => {
            const operationItem = operation.items.find(operationItem => operationItem.id === item.itemId)!
            return {
                itemId: item.itemId,
                productId: operationItem.productId,
                lotId: operationItem.lotId,
                pending: item.deliveryStats.pending,
                quantity: item.deliveryStats.pending
            }
        })
    , [deliveryOrder.items, operation.items])

    const [orderItems, setOrderItems] = useState<DeliveryItemRow[]>(defaultItemRows)
    const [deliveryNoteFile, setDeliveryNoteFile] = useState<File>()
    const [currentItem, setCurrentItem] = useState<DeliveryItemRow>()
    const [itemValidations, setItemValidations] = useState<string[]>([])
    const submittingRef = useRef(false)

    const totalQuantity = useMemo(
        () => orderItems.reduce((sum, item) => sum + item.quantity, 0),
        [orderItems]
    )

    const onDefineLot = (itemId: string) => () => {
        setCurrentItem(orderItems.find(item => item.itemId === itemId)!)
        openModal(ModalId.DEFINE_LOT_FORM)
    }

    const fields: FormField[] = [
        {
            name: 'operationCode',
            type: FieldType.TEXT,
            label: 'Operación',
            render: () => <>{describe(deliveryOrder.table, operation)}</>
        },
        {
            name: 'branchId',
            type: FieldType.TABLE,
            table: TableName.BRANCHES,
            label: `Sucursal de ${Labels.deliveryMethodLabel(deliveryOrder.method)}`,
            render: () => <BranchBadge branchId={deliveryOrder.branchId} />
        },
        {
            name: 'deliveryDate',
            type: FieldType.DATE,
            label: `Fecha de ${Labels.deliveryMethodLabel(deliveryOrder.method)}`,
            defaultValue: new Date(deliveryOrder.deliveryDate)
        },
        {
            name: 'items',
            type: FieldType.TABLE,
            render: () => (
                <Validatable validations={itemValidations} left>
                    <DeliveryOrderItemsTable
                        method={deliveryOrder.method}
                        operation={operation}
                        itemRows={orderItems}
                        title="Productos a Emitir"
                        onChange={setOrderItems}
                        onDefineLot={onDefineLot}
                    />
                </Validatable>
            )
        }
    ]
    // The supplier brings its own delivery note: we register its number and, optionally, a copy of it
    if (isReception) {
        const supplierDeliveryNoteNumberField = {
            name: 'supplierDeliveryNoteNumber',
            type: FieldType.TEXT,
            label: 'Nro. Remito del Proveedor'
        }
        const deliveryNoteField = {
            name: 'deliveryNoteUrl',
            type: FieldType.FILE,
            label: 'Remito del Proveedor',
            hint: 'Formato: PDF, JPG, PNG',
            optional: true,
            render: () => (
                <FileField
                    id="deliveryNoteUrl"
                    fileName={deliveryNoteFileName2(undefined, deliveryOrder)}
                    value={deliveryNoteFile}
                    fileTypes={[FileType.PDF, FileType.JPEG, FileType.PNG]}
                    onChange={setDeliveryNoteFile}
                />
            )
        }
        fields.splice(1, 0, supplierDeliveryNoteNumberField, deliveryNoteField)
    }

    const validate = async () => {
        if (!orderItems.some(item => item.quantity > 0)) {
            setItemValidations(['Ingresá al menos un producto con cantidad mayor a 0.'])
            return false
        }
        const validationTypes = isReception ? [ItemValidation.DEFINED_LOT] : [ItemValidation.DEFINED_LOT, ItemValidation.RESERVED_STOCK]
        const validations = await validateCompleteDeliveryItems(orderItems, validationTypes, deliveryOrder.branchId)
        setItemValidations(validations)
        return validations.length === 0
    }

    const onSubmit = async (data: Data2) => {
        // The validation loads stock before the spinner shows up, so a second click could submit the delivery twice
        if (submittingRef.current) return
        submittingRef.current = true
        try {
            if (await validate()) {
                await createDelivery(
                    deliveryOrder,
                    data.deliveryDate as Date,
                    orderItems,
                    deliveryNoteFile,
                    isReception ? (data.supplierDeliveryNoteNumber as string)?.trim() : undefined
                )
            }
        } finally {
            submittingRef.current = false
        }
    }

    const onCancel = () => navigate(-1)

    const onSubmitLot = (lotData: DeliveryItemLotData) => {
        setItemValidations([])
        const newOrderItems = orderItems.map(orderItem =>
            orderItem.itemId === currentItem?.itemId ? ({
                ...orderItem,
                lotId: lotData.lotId,
                lotData
            }) : orderItem
        )
        setOrderItems(newOrderItems)
    }

    return (
        <>
            <Form
                formId="delivery-form"
                fields={fields}
                createLabel={isReception ? 'Completar Recepción' : 'Generar Remito'}
                disableSubmit={totalQuantity === 0}
                onSubmit={onSubmit}
                onCancel={onCancel}
            />
            <DefineLotFormModal
                currentItem={currentItem}
                onSubmit={onSubmitLot}
            />
        </>
    )
}
